// services/FileService.js
import { projectCachedAxios, noCacheAxios, invalidateCache } from '../../../shared/config/cachedAxiosConfig';
import { FILE_ENDPOINTS, PRESIGNED_ENDPOINTS } from '../config/projectApiEndpoints';
import { getAuthHeaders } from '../../../shared/services/auth/authHeaders';
import { ProjectHeadersService } from './ProjectHeadersService';
import logger from '../../../shared/services/logging/logger';

export const FileService = {
  /**
   * Get all files in a folder
   */
  getFiles: async (projectId, path = '', userData, options = {}) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);

      const url = FILE_ENDPOINTS.getFiles(projectId, path);

      // options.noCache → use the cache-free axios instance so the listing is
      // always fetched live (reflects freshly created / deleted files).
      const client = options.noCache ? noCacheAxios : projectCachedAxios;
      const response = await client.get(url, { headers });
      
      if (Array.isArray(response.data) && response.data.length > 0) {
      }
      
      if (Array.isArray(response.data)) {
        // Convert response data to proper file objects
        const processedFiles = response.data.map((item, index) => {
          // If item is already a proper file object
          if (typeof item === 'object' && item !== null && item.name) {
            return {
              ...item,
              children: item.children || [],
              isLoaded: item.directory ? false : true,
              id: item.id || item.path || `${item.name}-${index}`
            };
          }
          
          // If item is a string (project name), convert to file object
          if (typeof item === 'string') {
            const isDirectory = item.endsWith('/') || !item.includes('.');
            return {
              name: item.replace(/\/$/, ''),
              directory: isDirectory,
              path: `projects/${projectId}/${path ? path + '/' : ''}${item}`,
              children: isDirectory ? [] : undefined,
              isLoaded: !isDirectory,
              id: `${item}-${index}`,
              content: isDirectory ? undefined : null
            };
          }
          
          // Fallback for unknown format
          return {
            name: String(item),
            directory: false,
            path: `projects/${projectId}/${path ? path + '/' : ''}${item}`,
            isLoaded: true,
            id: `unknown-${index}`,
            content: null
          };
        });
        
        // Sort the processed files
        const sortedFiles = processedFiles.sort((a, b) => {
          if (a.directory && !b.directory) return -1;
          if (!a.directory && b.directory) return 1;
          return a.name.localeCompare(b.name);
        });
        
        return sortedFiles;
      }
      
      return [];
    } catch (error) {
      throw error;
    }
  },


  /**
   * List files by S3 prefix — metadata only, no content downloaded.
   * Safe for large folders. Returns normalized file objects with name, size, lastModified.
   * Only returns files directly at the current folder level (subfolders are excluded).
   */
  listFilesByPrefix: async (projectId, folderPath, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      const prefix = `projects/${projectId}/${folderPath}/`;
      const url = FILE_ENDPOINTS.listByPrefix(projectId, prefix);
      console.log('[FileService.listFilesByPrefix] REQUEST', { url, prefix });
      const response = await noCacheAxios.get(url, { headers });
      console.log('[FileService.listFilesByPrefix] RESPONSE', { itemCount: Array.isArray(response.data) ? response.data.length : response.data });
      if (!Array.isArray(response.data)) return [];
      // Only return files at the current level (no slash after stripping prefix)
      return response.data
        .filter(item => {
          const relative = (item.s3Key || '').slice(prefix.length);
          return relative && !relative.includes('/');
        })
        .map(item => ({
          name: item.fileName,
          directory: false,
          size: item.size,
          lastModified: item.lastModified,
          id: item.s3Key,
          path: item.s3Key,
          isLoaded: true,
          content: null,
        }));
    } catch (err) {
      logger.warn('listFilesByPrefix failed', { error: err.message, folderPath });
      return [];
    }
  },

  /**
   * List ALL files under an S3 prefix, recursively (including nested subfolders).
   * Unlike listFilesByPrefix, this does NOT collapse to the current level — each
   * returned item includes `relative` (path below the prefix) so callers can group
   * by subfolder. Metadata only (name, size, lastModified). Returns [] on error.
   */
  listAllFilesByPrefix: async (projectId, folderPath, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      const prefix = `projects/${projectId}/${folderPath}/`;
      const url = FILE_ENDPOINTS.listByPrefix(projectId, prefix);
      const response = await noCacheAxios.get(url, { headers });
      if (!Array.isArray(response.data)) return [];
      return response.data.map(item => ({
        name: item.fileName,
        relative: (item.s3Key || '').slice(prefix.length),
        size: item.size,
        lastModified: item.lastModified,
        path: item.s3Key,
      }));
    } catch (err) {
      logger.warn('listAllFilesByPrefix failed', { error: err.message, folderPath });
      return [];
    }
  },

  /**
   * Fetch a single file's decoded content using the dedicated content endpoint.
   * Reliable alternative to getFileContent() — works when the listing API
   * does not return file content inline (e.g. large CSVs).
   * Returns the decoded string content, or throws on failure.
   */
  fetchFileContent: async (projectId, fileName, folderPath, userData) => {
    const baseHeaders = await getAuthHeaders();
    const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
    const url = FILE_ENDPOINTS.getFileContent(projectId, fileName, folderPath);
    const response = await noCacheAxios.get(url, { headers });
    const fileDto = response.data;
    if (!fileDto?.content) throw new Error('File has no content');
    return FileService.decodeBase64Content(fileDto.content);
  },

  /**
   * Get specific file content
   */
  getFileContent: async (projectId, filePath, userData) => {
    if (!projectId || !filePath) {
      const error = new Error('Project ID and file path are required');
      throw error;
    }
    
    // Extract folder path and filename
    const folderPath = filePath.includes('/') 
      ? filePath.substring(0, filePath.lastIndexOf('/'))
      : '';
    
    const fileName = filePath.includes('/')
      ? filePath.substring(filePath.lastIndexOf('/') + 1)
      : filePath;
    
    try {
      // Fetch all files in the folder
      const files = await FileService.getFiles(projectId, folderPath, userData);
      
      // Find the specific file
      const fileData = files.find(file => 
        file.name === fileName || 
        file.path?.endsWith(filePath)
      );
      
      if (!fileData) {
        throw new Error('File not found');
      }
      
      if (!fileData.content) {
        throw new Error('File has no content');
      }
      
      // Return file with decoded content
      const decodedContent = FileService.decodeBase64Content(fileData.content);
      
      const result = {
        ...fileData,
        decodedContent,
        rawContent: fileData.content
      };
      
      return result;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create a file in a folder - NOW USES MULTIPART/FORM-DATA
   */
  createFile: async (projectId, folderPath, fileData, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      
      // ✅ CRITICAL: Remove Content-Type header - let browser set it with boundary
      delete headers['Content-Type'];
      
      const formData = new FormData();
      
      // If fileData is already a File object
      if (fileData instanceof File) {
        formData.append('file', fileData);
      } 
      // If fileData is an object with name and content
      else if (fileData.name && fileData.content) {
        // Decode base64 content if present
        let fileContent;
        if (fileData.content.includes('base64,')) {
          // Handle data URL format
          const base64Data = fileData.content.split(',')[1];
          fileContent = atob(base64Data);
        } else {
          // Assume it's already base64
          try {
            fileContent = atob(fileData.content);
          } catch (e) {
            // If decode fails, use as-is
            fileContent = fileData.content;
          }
        }
        
        const blob = new Blob([fileContent], { type: 'application/octet-stream' });
        const file = new File([blob], fileData.name, { type: 'application/octet-stream' });
        formData.append('file', file);
      }
      
      const url = FILE_ENDPOINTS.createFile(projectId, folderPath);

      const response = await noCacheAxios.post(url, formData, { headers });

      // Invalidate project cache after file operation
      invalidateCache(projectCachedAxios);

      return response.data;
    } catch (error) {
      console.error('❌ Error creating file:', error);
      throw error;
    }
  },

  /**
   * Upload a file using presigned S3 URL — bypasses Lambda's 6 MB payload limit.
   * Step 1: POST to Lambda to obtain a short-lived presigned PUT URL.
   * Step 2: PUT file bytes directly to S3 (no auth headers — signature is in the URL).
   */
  uploadFile: async (projectId, folderPath, file, userData) => {
    try {
      if (!FileService.isFileTypeAllowed(file.name)) {
        throw new Error('Unsupported file type');
      }

      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);

      const cleanFolder = (folderPath || '').replace(/^\/+|\/+$/g, '');
      const s3Path = cleanFolder
        ? `projects/${projectId}/${cleanFolder}/${file.name}`
        : `projects/${projectId}/${file.name}`;

      // Step 1 — get presigned PUT URL from Lambda
      const presignedRes = await noCacheAxios.post(
        PRESIGNED_ENDPOINTS.presignedUpload(s3Path),
        null,
        { headers }
      );
      const { uploadUrl, s3Key } = presignedRes.data;

      // Step 2 — PUT file directly to S3 (no Authorization header — embedded in URL)
      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
      });

      if (!s3Response.ok) {
        throw new Error(`S3 upload failed with status ${s3Response.status}`);
      }

      invalidateCache(projectCachedAxios);
      logger.info('File uploaded via presigned URL', { fileName: file.name, s3Key });

      return { s3Key, fileName: file.name };
    } catch (error) {
      logger.error('File upload failed', { fileName: file?.name, error: error.message });

      const status = error.response?.status;
      const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');

      if (status === 413 || isNetworkError) {
        const err = new Error('UPLOAD_FAILED');
        err.isUploadFailed = true;
        throw err;
      }

      throw error;
    }
  },

  /**
   * Get a presigned S3 GET URL for direct file download — bypasses Lambda's 6 MB response limit.
   * Returns { url, fileName, expiresInSeconds }.
   */
  getPresignedDownloadUrl: async (s3Key, userData) => {
    const baseHeaders = await getAuthHeaders();
    const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
    const endpoint = PRESIGNED_ENDPOINTS.presignedDownload(s3Key);
    try {
      const response = await noCacheAxios.get(endpoint, { headers });
      logger.debug('getPresignedDownloadUrl: got URL', { s3Key, hasUrl: !!response.data?.url });
      return response.data;
    } catch (err) {
      logger.error('getPresignedDownloadUrl: request failed', {
        s3Key, endpoint, error: err.message, status: err.response?.status, responseData: err.response?.data,
      });
      throw err;
    }
  },

  /**
   * Fetch a text/JSON/CSV file directly from S3 via presigned download.
   * Returns the raw file content as a string (no Lambda, no base64 round-trip).
   */
  fetchTextFile: async (s3Key, userData) => {
    const { url } = await FileService.getPresignedDownloadUrl(s3Key, userData);
    const response = await fetch(url);
    if (!response.ok) {
      logger.error('fetchTextFile: S3 GET failed', { s3Key, status: response.status, statusText: response.statusText });
      throw new Error(`Failed to fetch file: ${response.status}`);
    }
    const text = await response.text();
    logger.debug('fetchTextFile: fetched', { s3Key, byteLength: text.length });
    return text;
  },

  /**
   * Fetch file content via presigned download for in-app preview.
   * Returns { rawContent: base64String, decodedContent: string|null }
   * matching the shape expected by the FilePreview component.
   */
  fetchFilePreview: async (s3Key, userData) => {
    const { url } = await FileService.getPresignedDownloadUrl(s3Key, userData);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch file preview: ${response.status}`);
    const arrayBuffer = await response.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);

    // Build base64 string for binary previews (images, PDFs, DOCX)
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const rawContent = btoa(binary);

    // Decode as UTF-8 text for text/code/JSON/CSV previews
    let decodedContent = null;
    try {
      decodedContent = new TextDecoder('utf-8').decode(bytes);
    } catch (e) {
      // Binary file — leave decodedContent null
    }

    return { rawContent, decodedContent };
  },

  /**
   * Save/update file content - NOW USES MULTIPART/FORM-DATA
   */
  saveFileContent: async (projectId, filePath, content, userData) => {
    try {
      const pathSegments = filePath.split('/');
      const fileName = pathSegments.pop();
      const folderPath = pathSegments.join('/');

      const blob = new Blob([content], { type: 'text/plain' });
      const file = new File([blob], fileName, { type: 'text/plain' });

      return await FileService.uploadFile(projectId, folderPath, file, userData);
    } catch (error) {
      console.error('❌ Error saving file:', error);
      throw error;
    }
  },


  /**
   * Delete a file
   */
  deleteFile: async (projectId, filePath, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      
      // Extract folder path and filename
      const folderPath = filePath.includes('/') 
        ? filePath.substring(0, filePath.lastIndexOf('/'))
        : '';
      
      const fileName = filePath.includes('/')
        ? filePath.substring(filePath.lastIndexOf('/') + 1)
        : filePath;
      
      // Build the URL with fileName as a query parameter
      const baseUrl = FILE_ENDPOINTS.createFile(projectId, folderPath);
      const url = `${baseUrl}&fileName=${encodeURIComponent(fileName)}`;
      
      // Use DELETE method with fileName as query parameter
      const response = await noCacheAxios.delete(url, { headers });

      // Invalidate project cache after deleting file
      invalidateCache(projectCachedAxios);

      return response.data;
    } catch (error) {
      
      // Enhanced error details
      if (error.response) {
      }
      
      throw error;
    }
  },
  
  // ... rest of your utility methods remain unchanged
  fileToBase64: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  },

  decodeBase64Content: (base64Content) => {
    try {
      const binary = atob(base64Content);
      const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
      return new TextDecoder('utf-8').decode(bytes);
    } catch (error) {
      return 'Error decoding file content';
    }
  },

  getFileLanguage: (filename) => {
    if (!filename) return 'text';
    
    const extension = filename.split('.').pop().toLowerCase();
    
    const extensionMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'html': 'html',
      'css': 'css',
      'json': 'json',
      'java': 'java',
      'py': 'python',
      'rb': 'ruby',
      'php': 'php',
      'c': 'c',
      'cpp': 'cpp',
      'cs': 'csharp',
      'go': 'go',
      'rs': 'rust',
      'swift': 'swift',
      'kt': 'kotlin',
      'md': 'markdown',
      'yml': 'yaml',
      'yaml': 'yaml',
      'xml': 'xml',
      'sql': 'sql',
      'sh': 'bash',
      'bat': 'batch',
      'ps1': 'powershell',
      'feature': 'gherkin',
      'properties': 'properties'
    };
    
    return extensionMap[extension] || 'text';
  },

  isBinaryFile: (content, fileName) => {
    const binaryExtensions = [
      'jpg', 'jpeg', 'png', 'gif', 'bmp', 'ico', 'svg',
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
      'zip', 'tar', 'gz', 'rar', '7z',
      'exe', 'dll', 'so', 'class', 'jar'
    ];
    
    const extension = fileName.split('.').pop().toLowerCase();
    if (binaryExtensions.includes(extension)) {
      return true;
    }
    
    if (content && content.includes('\0')) {
      return true;
    }
    
    return false;
  },

  isFileTypeAllowed: (fileName) => {
    if (!fileName) return false;
    
    const allowedExtensions = [
      // Text files
      '.txt', '.log', '.json', '.xml', '.yaml', '.yml',
      '.md', '.csv', '.tsv', '.java', '.py', '.js', '.jsx',
      '.ts', '.tsx', '.html', '.css', '.scss', '.less',
      '.php', '.rb', '.c', '.cpp', '.h', '.cs', '.go',
      '.rs', '.swift', '.kt', '.sql', '.sh', '.bat', '.feature',
      // Config files
      '.properties', '.config', '.conf', '.cfg', '.ini', '.env',
      // Copybook files
      '.cpy', '.cob', '.copybook',
      // Diagram files
      '.mermaid', '.mmd', '.drawio', '.puml',
      // Images
      '.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp', '.bmp', '.ico', '.tiff',
      // Documents
      '.pdf', '.doc', '.docx', '.dotx', '.rtf',
      // Spreadsheets
      '.xlsx', '.xls', '.xlsm',
      // Presentations
      '.pptx', '.ppt', '.potx',
      // Archives
      '.zip', '.rar', '.7z', '.tar', '.gz',
      // Mobile app packages
      '.apk', '.ipa', '.aab',
      // Video files
      '.mp4', '.mov', '.avi', '.webm', '.mkv'
    ];
    
    const fileExtension = '.' + fileName.split('.').pop().toLowerCase();
    return allowedExtensions.includes(fileExtension);
  }
};